import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Target, 
  Users, 
  Search,
  Code,
  ArrowRight,
  CheckCircle,
  BarChart3,
  Smartphone,
  Globe,
  Mail,
  Camera,
  PenTool
} from 'lucide-react';
import Button from '../components/Button';

const Services = () => {
  const services = [
    {
      id: 'digital-marketing',
      icon: TrendingUp,
      title: 'Digital Marketing',
      description: 'Comprehensive digital marketing strategies to boost your online presence and drive qualified leads to your business.',
      features: [
        'Search Engine Optimization (SEO)',
        'Content Marketing & Strategy',
        'Email Marketing Campaigns',
        'Marketing Automation',
        'Analytics & Performance Tracking',
        'Conversion Rate Optimization'
      ],
      benefits: [
        'Increased online visibility',
        'Higher quality leads',
        'Better brand awareness',
        'Improved ROI on marketing spend'
      ],
      process: [
        'Strategy Development',
        'Campaign Implementation',
        'Performance Monitoring',
        'Optimization & Scaling'
      ]
    },
    {
      id: 'meta-ads',
      icon: Target,
      title: 'Meta Ads (Facebook & Instagram)',
      description: 'High-converting Facebook and Instagram ad campaigns that reach your target audience effectively and drive results.',
      features: [
        'Campaign Strategy & Planning',
        'Creative Design & Copywriting',
        'Advanced Audience Targeting',
        'A/B Testing & Optimization',
        'Retargeting Campaigns',
        'Performance Analytics'
      ],
      benefits: [
        'Precise audience targeting',
        'High-quality ad creatives',
        'Improved conversion rates',
        'Better return on ad spend'
      ],
      process: [
        'Audience Research',
        'Creative Development',
        'Campaign Launch',
        'Optimization & Scaling'
      ]
    },
    {
      id: 'social-media',
      icon: Users,
      title: 'Social Media Management',
      description: 'Engaging social media management that builds your brand, connects with your audience, and drives engagement.',
      features: [
        'Content Creation & Curation',
        'Community Management',
        'Influencer Partnerships',
        'Social Media Advertising',
        'Brand Monitoring',
        'Social Analytics & Reporting'
      ],
      benefits: [
        'Stronger brand presence',
        'Increased engagement',
        'Better customer relationships',
        'Higher brand loyalty'
      ],
      process: [
        'Strategy Development',
        'Content Planning',
        'Community Building',
        'Performance Analysis'
      ]
    },
    {
      id: 'google-ads',
      icon: Search,
      title: 'Google Ads',
      description: 'Strategic Google Ads campaigns that capture high-intent customers and maximize your advertising ROI.',
      features: [
        'Search Campaign Management',
        'Display Network Advertising',
        'Shopping Campaigns',
        'YouTube Advertising',
        'Local Business Ads',
        'Performance Tracking'
      ],
      benefits: [
        'High-intent traffic',
        'Immediate visibility',
        'Precise targeting options',
        'Measurable results'
      ],
      process: [
        'Keyword Research',
        'Campaign Setup',
        'Bid Management',
        'Performance Optimization'
      ]
    },
    {
      id: 'web-development',
      icon: Code,
      title: 'Web Development',
      description: 'Custom web development solutions that are fast, responsive, and optimized for conversions and user experience.',
      features: [
        'Custom Website Development',
        'E-commerce Solutions',
        'Mobile-First Design',
        'Performance Optimization',
        'SEO-Friendly Development',
        'Maintenance & Support'
      ],
      benefits: [
        'Professional online presence',
        'Better user experience',
        'Higher conversion rates',
        'Mobile-optimized design'
      ],
      process: [
        'Planning & Design',
        'Development & Testing',
        'Launch & Optimization',
        'Ongoing Maintenance'
      ]
    }
  ];

  const additionalServices = [
    {
      icon: BarChart3,
      title: 'Analytics & Reporting',
      description: 'Comprehensive analytics and detailed reporting to track your digital marketing performance.'
    },
    {
      icon: Smartphone,
      title: 'Mobile Marketing',
      description: 'Mobile-first marketing strategies to reach your audience on their preferred devices.'
    },
    {
      icon: Globe,
      title: 'Local SEO',
      description: 'Local search optimization to help your business get found by customers in your area.'
    },
    {
      icon: Mail,
      title: 'Email Marketing',
      description: 'Strategic email campaigns to nurture leads and retain customers effectively.'
    },
    {
      icon: Camera,
      title: 'Content Creation',
      description: 'Professional content creation including photography, videography, and graphic design.'
    },
    {
      icon: PenTool,
      title: 'Brand Design',
      description: 'Complete brand identity design including logos, guidelines, and marketing materials.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Services - Digital Marketing & Web Development | Subtronz</title>
        <meta name="description" content="Comprehensive digital marketing services including SEO, social media, Google Ads, Meta Ads, and web development. Get results with our expert team." />
        <meta name="keywords" content="digital marketing services, SEO, social media marketing, google ads, facebook ads, web development, meta ads" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-orange-50 to-purple-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-secondary mb-6">
              Our <span className="gradient-text">Services</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              We offer comprehensive digital marketing and web development services 
              to help your business grow and succeed in the digital world.
            </p>
            <Button size="lg" href="/contact">
              Get Started Today
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
              Our Core Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive digital solutions designed to drive growth and maximize your online potential.
            </p>
          </motion.div>

          <div className="space-y-24">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  id={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                  }`}
                >
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mr-4">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-3xl font-bold text-secondary">{service.title}</h3>
                    </div>
                    <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                      <div>
                        <h4 className="font-semibold text-secondary mb-3">What We Offer</h4>
                        <ul className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center text-sm text-gray-600">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-secondary mb-3">Benefits</h4>
                        <ul className="space-y-2">
                          {service.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-center text-sm text-gray-600">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-secondary mb-3">Our Process</h4>
                        <ul className="space-y-2">
                          {service.process.map((step, idx) => (
                            <li key={idx} className="flex items-center text-sm text-gray-600">
                              <div className="w-4 h-4 bg-primary rounded-full mr-2 flex-shrink-0 flex items-center justify-center">
                                <span className="text-white text-xs font-bold">{idx + 1}</span>
                              </div>
                              {step}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <Button href="/contact">
                      Learn More
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>

                  <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <div className="bg-gradient-to-br from-primary to-purple rounded-2xl p-8 text-white">
                      <div className="text-center">
                        <Icon className="w-16 h-16 mx-auto mb-4" />
                        <h4 className="text-2xl font-bold mb-4">Ready to Get Started?</h4>
                        <p className="text-orange-100 mb-6">
                          Let's discuss how {service.title.toLowerCase()} can help your business grow.
                        </p>
                        <Button variant="secondary" href="/contact#contact-form">
                          Get Free Consultation
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
              Additional Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We also offer specialized services to complement your digital marketing strategy.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-secondary mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
              How We Work
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our proven process ensures consistent results and successful project delivery.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'We start by understanding your business, goals, and target audience.'
              },
              {
                step: '02',
                title: 'Strategy',
                description: 'We develop a customized strategy tailored to your specific needs.'
              },
              {
                step: '03',
                title: 'Implementation',
                description: 'Our team executes the strategy with precision and attention to detail.'
              },
              {
                step: '04',
                title: 'Optimization',
                description: 'We continuously monitor and optimize for better results.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-secondary mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary via-accent to-purple">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Digital Presence?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Let's discuss your project and see how our services can help you achieve your goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="lg" href="/contact#contact-form">
                Get Free Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button variant="outline" size="lg" href="/pricing" className="border-white text-white hover:bg-white hover:text-primary">
                View Pricing
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services;
